import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import countriesLib from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';
import { useFilters } from '../../contexts/FiltersContext';
import { useAnalyticsData } from '../../contexts/AnalyticsDataContext';
import { useAnalyticsRefresh } from '../../contexts/AnalyticsRefreshContext';
import analyticsService from '../../services/analyticsApi';
import { useSplitView } from '../../contexts/SplitViewContext';
import {
  Box,
  Typography,
  Grid,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  useTheme,
  Theme,
  alpha,
  Tooltip as MuiTooltip,
  ToggleButton,
  ToggleButtonGroup,
  Skeleton,
} from '@mui/material';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts';
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup,
  Line as MapLine,
  Marker as MapMarker,
} from 'react-simple-maps';
import {
  Co2 as Co2Icon,
  Close as CloseIcon,
} from '@mui/icons-material';
import ProfessionalKPICard from '../../components/common/ProfessionalKPICard';
import ProfessionalChartCard from '../../components/common/ProfessionalChartCard';
import { getContinent, VALID_CONTINENTS, CONTINENT_COORDINATES } from '../../utils/continentMapping';
import { getCountryCode } from '../../utils/countryCodeMapping';
import { formatChartNumber, formatNumber, formatStringValue } from '../../utils/numberFormat';
import * as topojson from 'topojson-client';

// Professional color palette
const CHART_COLORS = {
  primary: '#90BF45',
  secondary: '#6C757D',
  success: '#28a745',
  info: '#17a2b8',
  warning: '#ffc107',
  danger: '#dc3545',
  gradient: ['#90BF45', '#6C757D', '#17a2b8', '#ffc107', '#28a745', '#dc3545'],
  chartColors: ['#6BAED6', '#FDAE6B', '#74C476', '#FB6A6A', '#9E9AC8', '#C49C94', '#F2A7C6', '#BDBDBD', '#C7C94D', '#6ED0E0'],
  optionBG: '#eff6e9'
};

interface TabInfo {
  id: string;
  name: string;
  description: string;
  icon: string;
}

countriesLib.registerLocale(enLocale);

const alpha3ToAlpha2 = (code: string): string => {
  if (code?.length !== 3) return code;
  const alpha2 = countriesLib.alpha3ToAlpha2(code.toUpperCase());
  return alpha2 || code;
};

const formatAnalyticsLabel = (label: string): string => {
  return label === 'Customers' ? 'Sub-Customers' : label;
};

// Convert country code in value (e.g., "IND with 100.00%" -> "IN with 100.00%")
const convertValueCountryCode = (val: string | number): string | number => {
  if (typeof val !== 'string') return val;
  const match = /^([A-Z]{3})\s/.exec(val);
  if (match) {
    const alpha2 = alpha3ToAlpha2(match[1]);
    return val.replace(/^[A-Z]{3}\s/, `${alpha2} `);
  }
  return val;
};

// Backend writes canonical lowercase modes; just uppercase them for display.
const convertTransportModeAlias = (val: string | number): string | number => {
  if (typeof val !== 'string') return val;
  return val.replaceAll(/\b(air|sea|rail|road|hub|ferry|iww)\b/gi, (m) => m.toUpperCase());
};

type DoughnutSelection = {
  chartKey: string;
  index: number;
} | null;

const VALID_TIME_PERIODS = new Set(['YTD', 'MTD', 'L12M', 'HISTORIC']);

// Backend now writes canonical lowercase mode names (road / air / sea /
// iww / ferry / rail / hub) — see refresh_aggregates_batch in
// 20260429000005_fix_geography_source_in_aggregates.js. Just uppercase
// mode strings for display; no more legacy "Truck → ROAD" /
// "Transshipment → HUB" branches needed.
const TRANSPORT_MODES = new Set(['air', 'sea', 'rail', 'road', 'hub', 'ferry', 'iww']);

const aliasMode = (raw: string): string => {
  const lc = raw.toLowerCase();
  return TRANSPORT_MODES.has(lc) ? lc.toUpperCase() : raw;
};

// Normalizes a single chart datum: uppercases transport-mode names/keys and,
// on the Sub-Customers tab, relabels "main" to "NA".
export const aliasChartDatum = (item: any, isCustomersTab: boolean): any => {
  if (!item || typeof item !== 'object') return item;
  const next: any = { ...item };
  if (typeof next.name === 'string') {
    next.name = aliasMode(next.name);
    // Doughnut data uses { name, value } — relabel the slice name
    if (isCustomersTab && next.name.toLowerCase().includes('main')) {
      next.name = 'NA';
    }
  }
  // Rename mode-named keys (multiline datasets use mode names as keys)
  for (const key of Object.keys(next)) {
    if (TRANSPORT_MODES.has(key.toLowerCase())) {
      const newKey = aliasMode(key);
      if (newKey !== key) {
        next[newKey] = next[key];
        delete next[key];
      }
    } else if (isCustomersTab && key !== 'name' && key.toLowerCase().includes('main')) {
      // Multiline/multibar datasets use the sub-customer name as a series key
      next['NA'] = next[key];
      delete next[key];
    }
  }
  return next;
};

// Returns a copy of chartConfig with its data array normalized via
// aliasChartDatum, or the original config when there is no data array.
export const aliasChartConfigData = (chartConfig: any, isCustomersTab: boolean): any => {
  if (!chartConfig?.data) return chartConfig;
  return {
    ...chartConfig,
    data: chartConfig.data.map((item: any) => aliasChartDatum(item, isCustomersTab)),
  };
};

const COUNTRY_TOPOLOGY_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

// Builds the merged continent GeoJSON features from a world-atlas topology.
export const buildContinentFeatures = (geoTopology: any): any[] => {
  const countries = geoTopology.objects.countries.geometries;
  return VALID_CONTINENTS.map((continent) => {
    const continentCountries = countries.filter((c: any) => {
      const name = c.properties.name?.toLowerCase();
      const mapped = getContinent(name);
      if (continent === 'Americas') {
        return mapped === 'North America' || mapped === 'South America';
      }
      return mapped === continent;
    });

    if (continentCountries.length === 0) return null;

    const merged = topojson.merge(geoTopology, continentCountries);
    return {
      ...merged,
      properties: {
        name: continent, // Allow lookup by "name" (legacy intent)
        continent: continent,
      },
    };
  }).filter(Boolean);
};

// Resolves the data value for a geography, applying the country-code and
// continent fallbacks used for non-continent (country-level) maps.
export const resolveGeoValue = (
  dataMap: Map<any, any>,
  geo: any,
  isContinentData: boolean,
): { lookupName: string | undefined; value: any } => {
  const countryName = geo.properties.name?.toLowerCase();
  const continentName = geo.properties.continent;
  const lookupName = isContinentData ? continentName : countryName;

  if (!lookupName) return { lookupName: undefined, value: undefined };

  let value = dataMap.get(lookupName.toLowerCase());

  if (!isContinentData) {
    const countryCode = getCountryCode(countryName);
    const continent = getContinent(countryName);
    if (value === undefined && countryCode) {
      value = dataMap.get(countryCode.toLowerCase());
    }
    if (value === undefined && continent) {
      value = dataMap.get(continent.toLowerCase());
    }
  }

  return { lookupName, value };
};

// Resolves the legend datum -> slice index used by the interactive doughnut.
export const getDoughnutLegendSliceIndex = (data: any[], entry: any, fallbackIndex: number): number => {
  const legendPayload = entry?.payload?.payload ?? entry?.payload ?? entry;
  const legendName = legendPayload?.name ?? entry?.value;
  const legendValue = Number(legendPayload?.value ?? entry?.payload?.value);

  const matchedIndex = data.findIndex((item: any) => {
    const itemValue = Number(item?.value);
    return item?.name === legendName && itemValue === legendValue;
  });

  return matchedIndex >= 0 ? matchedIndex : fallbackIndex;
};

// recharts pie Tooltip formatter: "<value> (<pct>%)" plus the series name.
export const pieTooltipFormatter = (totalValue: number) => (val: any, name: any): [string, any] => {
  const numVal = Number(val) || 0;
  const percent = totalValue > 0 ? (numVal / totalValue * 100).toFixed(1) : '0';
  return [`${formatNumber(numVal, 2)} (${percent}%)`, name];
};

// recharts pie Legend formatter: truncates long labels and appends the pct.
export const pieLegendFormatter = (totalValue: number) => (value: string, entry: any): string => {
  const itemValue = Number(entry?.payload?.value) || 0;
  const pct = totalValue > 0 ? (itemValue / totalValue * 100).toFixed(0) : '0';
  const dn = value.length > 20 ? value.substring(0, 20) + '...' : value;
  return `${dn} (${pct}%)`;
};

export const continentLegendWrapperStyle = (chartName: string | undefined, isEnlarged: boolean) =>
  chartName === 'Top Flows (Continents)'
    ? {
        paddingTop: 0,
        bottom: isEnlarged ? 5 : 10,
        height: isEnlarged ? 'auto' : 90,
        fontSize: 'x-small',
      }
    : { paddingTop: 20 };

type ResolvedDefaults = { tabId: string | null; timeFilter: string | null };

// Pure resolution of the initial tab id and time filter from the (optional)
// default-filter response plus URL params. Priority for both: URL > DB > none.
// `dbResponse` is the `data.response` object when the call succeeded with a
// usable payload, otherwise null (non-200, missing response, or an error).
export const resolveInitDefaults = (
  dbResponse: { analytics_tab_id?: string | null; time_period?: string | null } | null,
  urlTab: string | null,
  urlType: string | null,
): ResolvedDefaults => {
  let tabId: string | null = null;
  if (urlTab) {
    tabId = urlTab;
  } else if (dbResponse?.analytics_tab_id) {
    tabId = dbResponse.analytics_tab_id;
  }

  const typeToUse = urlType || dbResponse?.time_period;
  const timeFilter = typeToUse && VALID_TIME_PERIODS.has(typeToUse) ? typeToUse : null;

  return { tabId, timeFilter };
};

// Pie chart view. Extracted verbatim from the renderChart 'pie' case.
export const PieChartView: React.FC<{ chartConfig: any; height: number; isEnlarged: boolean; theme: Theme }> = ({
  chartConfig,
  height,
  isEnlarged,
  theme,
}) => {
  const totalValue = chartConfig.data.reduce((sum: number, item: any) => sum + (item.value || 0), 0);

  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        {/* Per-slice colour travels on each datum's `fill`, replacing the deprecated <Cell>. */}
        <Pie
          data={chartConfig.data.map((item: any, index: number) => ({
            ...item,
            fill: CHART_COLORS.chartColors[index] ?? CHART_COLORS.primary,
          }))}
          dataKey="value"
          nameKey="name"
          cx="50%"
          cy="50%"
          outerRadius={isEnlarged ? 180 : 100}
          innerRadius={0}
          label={false}
          labelLine={false}
        />
        <Tooltip
          contentStyle={{
            background: 'white',
            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
            borderRadius: 8,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          }}
          formatter={pieTooltipFormatter(totalValue)}
        />
        <Legend
          wrapperStyle={continentLegendWrapperStyle(chartConfig.name, isEnlarged)}
          formatter={pieLegendFormatter(totalValue)}
        />
      </PieChart>
    </ResponsiveContainer>
  );
};

type DoughnutChartViewProps = {
  chartConfig: any;
  height: number;
  isEnlarged: boolean;
  chartInstanceKey: string;
  theme: Theme;
  activeDoughnutSlice: DoughnutSelection;
  setHoveredDoughnutSlice: React.Dispatch<React.SetStateAction<DoughnutSelection>>;
  setSelectedDoughnutSlice: React.Dispatch<React.SetStateAction<DoughnutSelection>>;
};

type DoughnutLegendContentProps = {
  payload?: any[];
  data: any[];
  totalValue: number;
  activeIndex: number | undefined;
  theme: Theme;
  onSliceHover: (index: number | null) => void;
  onSliceSelect: (index: number) => void;
};

// Custom doughnut legend body. recharts passes `payload`; the remaining props
// are bound by DoughnutChartView. Exported so the interactive legend logic can
// be unit-tested directly (recharts content render-props do not fire in jsdom).
export const DoughnutLegendContent: React.FC<DoughnutLegendContentProps> = ({
  payload,
  data,
  totalValue,
  activeIndex,
  theme,
  onSliceHover,
  onSliceSelect,
}) => {
  if (!payload?.length) return null;

  const renderItem = (entry: any, index: number) => {
    const sliceIndex = getDoughnutLegendSliceIndex(data, entry, index);
    const itemValue = Number(data[sliceIndex]?.value ?? entry?.payload?.value) || 0;
    const pct = totalValue > 0 ? (itemValue / totalValue * 100).toFixed(0) : '0';
    const label = `${entry?.value ?? ''} (${pct}%)`;
    const isActive = activeIndex === sliceIndex;
    const sliceColor = CHART_COLORS.chartColors[sliceIndex] ?? entry?.color ?? CHART_COLORS.primary;

    return (
      <Box
        key={`legend-item-${sliceIndex}-${entry?.value ?? ''}`}
        onMouseEnter={() => onSliceHover(sliceIndex)}
        onClick={() => onSliceSelect(sliceIndex)}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.25,
          py: 0.75,
          borderRadius: 999,
          cursor: 'pointer',
          userSelect: 'none',
          transition: 'all 0.2s ease',
          border: `1px solid ${isActive ? alpha(CHART_COLORS.primary, 0.5) : alpha(theme.palette.divider, 0.2)}`,
          backgroundColor: isActive ? alpha(CHART_COLORS.primary, 0.08) : alpha(theme.palette.background.paper, 0.9),
          boxShadow: isActive ? `0 6px 16px ${alpha(CHART_COLORS.primary, 0.12)}` : 'none',
          '&:hover': {
            backgroundColor: alpha(CHART_COLORS.primary, 0.1),
          },
        }}
      >
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            backgroundColor: sliceColor,
            boxShadow: isActive ? `0 0 0 3px ${alpha(sliceColor, 0.18)}` : 'none',
          }}
        />
        <Typography
          variant="caption"
          sx={{
            fontWeight: isActive ? 700 : 500,
            color: isActive ? 'text.primary' : 'text.secondary',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </Typography>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        mt: 2,
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1,
        justifyContent: 'center',
        alignItems: 'center',
      }}
      onMouseLeave={() => onSliceHover(null)}
    >
      {payload.map((entry: any, index: number) => renderItem(entry, index))}
    </Box>
  );
};

type DoughnutTooltipContentProps = {
  data: any[];
  totalValue: number;
  activeIndex: number | undefined;
  theme: Theme;
};

// Custom doughnut tooltip body for the active slice. Exported for direct
// unit-testing (see note on DoughnutLegendContent).
export const DoughnutTooltipContent: React.FC<DoughnutTooltipContentProps> = ({
  data,
  totalValue,
  activeIndex,
  theme,
}) => {
  if (activeIndex === undefined) return null;

  const activeItem = data[activeIndex];
  if (!activeItem) return null;

  const numVal = Number(activeItem.value) || 0;
  const percent = totalValue > 0 ? (numVal / totalValue * 100).toFixed(1) : '0';

  return (
    <Box
      sx={{
        background: 'white',
        border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
        borderRadius: 1,
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        px: 1.5,
        py: 1,
      }}
    >
      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
        {activeItem.name}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
        {formatNumber(numVal, 2)} ({percent}%)
      </Typography>
    </Box>
  );
};

// Interactive doughnut chart view. Extracted verbatim from the renderChart
// 'doughnut' case; its former inline legend/tooltip closures are now the
// DoughnutLegendContent / DoughnutTooltipContent components.
export const DoughnutChartView: React.FC<DoughnutChartViewProps> = ({
  chartConfig,
  height,
  isEnlarged,
  chartInstanceKey,
  theme,
  activeDoughnutSlice,
  setHoveredDoughnutSlice,
  setSelectedDoughnutSlice,
}) => {
  const totalValue = chartConfig.data.reduce((sum: number, item: any) => sum + (item.value || 0), 0);
  const chartKey = `${chartInstanceKey}:${chartConfig.name || 'doughnut-chart'}`;
  const activeSlice = activeDoughnutSlice;
  const activeIndex = activeSlice?.chartKey === chartKey ? activeSlice.index : undefined;
  const setHoverState = (index: number | null) => {
    setHoveredDoughnutSlice(index === null ? null : { chartKey, index });
  };
  const setSelectedState = (index: number) => {
    setSelectedDoughnutSlice({ chartKey, index });
    setHoveredDoughnutSlice({ chartKey, index });
  };
  const renderDoughnutLegend = ({ payload }: any) => (
    <DoughnutLegendContent
      payload={payload}
      data={chartConfig.data}
      totalValue={totalValue}
      activeIndex={activeIndex}
      theme={theme}
      onSliceHover={setHoverState}
      onSliceSelect={setSelectedState}
    />
  );
  const renderDoughnutTooltip = () => (
    <DoughnutTooltipContent
      data={chartConfig.data}
      totalValue={totalValue}
      activeIndex={activeIndex}
      theme={theme}
    />
  );

  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        {/*
          Per-slice styling (incl. the active-hover highlight) travels on each datum,
          replacing the deprecated <Cell>. recharts merges these into the sector exactly
          as it did the Cell props, so hover behaviour is unchanged.
        */}
        <Pie
          data={chartConfig.data.map((item: any, index: number) => ({
            ...item,
            fill: CHART_COLORS.chartColors[index] ?? CHART_COLORS.primary,
            stroke: activeIndex === index ? CHART_COLORS.primary : 'transparent',
            strokeWidth: activeIndex === index ? 3 : 0,
            opacity: activeIndex === undefined || activeIndex === index ? 1 : 0.35,
          }))}
          dataKey="value"
          nameKey="name"
          cx="50%"
          cy="50%"
          outerRadius={isEnlarged ? 180 : 100}
          innerRadius={isEnlarged ? 110 : 60}
          label={false}
          labelLine={false}
          onMouseEnter={(_, index) => setHoverState(index)}
          onMouseLeave={() => setHoverState(null)}
        />
        <Tooltip
          key={`${chartKey}-tooltip-${activeIndex ?? 'none'}`}
          active={activeIndex !== undefined}
          defaultIndex={activeIndex}
          content={renderDoughnutTooltip}
        />
        <Legend
          wrapperStyle={continentLegendWrapperStyle(chartConfig.name, isEnlarged)}
          content={renderDoughnutLegend}
        />
      </PieChart>
    </ResponsiveContainer>
  );
};

type MapChartViewProps = {
  chartConfig: any;
  theme: Theme;
  continentFeatures: any[];
  geoTopology: any;
  setContinentFeatures: React.Dispatch<React.SetStateAction<any[]>>;
};

// Choropleth + flow map view. Extracted verbatim from the renderChart 'map' case.
export const MapChartView: React.FC<MapChartViewProps> = ({
  chartConfig,
  theme,
  continentFeatures,
  geoTopology,
  setContinentFeatures,
}) => {
  // Detect if the data is primarily about continents
  const isContinentData = chartConfig.data.some((item: any) => VALID_CONTINENTS.includes(item.name));

  // Prepare map data source
  let mapData = null;
  if (isContinentData) {
    if (continentFeatures.length === 0 && geoTopology) {
      const features = buildContinentFeatures(geoTopology);
      setContinentFeatures(features);
      mapData = features;
    } else {
      mapData = continentFeatures;
    }
  } else {
    // React Simple Maps handles URL caching well; stay consistent with the
    // previous code that used the topology URL for country-level maps.
    mapData = COUNTRY_TOPOLOGY_URL;
  }

  // Create a map of country names to values for quick lookup
  const dataMap = new Map(chartConfig.data.map((item: any) => [item.name?.toLowerCase(), item.value]));

  // Calculate max value for color scaling
  const maxValue = Math.max(...chartConfig.data.map((item: any) => item.value || 0));

  // Function to get color based on value
  const getColor = (geo: any) => {
    const { lookupName, value } = resolveGeoValue(dataMap, geo, isContinentData);
    if (!lookupName) return alpha(theme.palette.divider, 0.1);
    if (!value) return alpha(theme.palette.divider, 0.1);
    const intensity = (value as number) / maxValue;
    return alpha(CHART_COLORS.primary, 0.3 + intensity * 0.7);
  };

  const renderGeography = (geo: any) => {
    const { lookupName, value } = resolveGeoValue(dataMap, geo, isContinentData);
    // skip if no valid name found (e.g. Antarctica in some maps might differ)
    if (!lookupName) return null;

    return (
      <MuiTooltip
        key={geo.rsmKey || geo.properties.name}
        title={
          <Box sx={{ p: 0.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'inherit' }}>
              {lookupName.charAt(0).toUpperCase() + lookupName.slice(1)}
              {value !== undefined && `: ${formatNumber(value as number, 2)}`}
            </Typography>
          </Box>
        }
        componentsProps={{
          tooltip: {
            sx: {
              bgcolor: 'grey.800',
              '& .MuiTooltip-arrow': {
                color: 'grey.800',
              },
              color: 'common.white',
              boxShadow: theme.shadows[4],
            },
          },
        }}
        arrow
        placement="top"
      >
        <Geography
          geography={geo}
          fill={getColor(geo)}
          stroke={alpha(theme.palette.divider, 0.3)}
          strokeWidth={0.5}
          style={{
            default: {
              fill: getColor(geo),
              outline: 'none',
            },
            hover: {
              fill: getColor(geo),
              outline: 'none',
              cursor: value ? 'pointer' : 'default',
            },
            pressed: {
              fill: getColor(geo),
              outline: 'none',
            },
          }}
        />
      </MuiTooltip>
    );
  };

  const renderFlow = (flow: any, index: number) => {
    const fromCoord = CONTINENT_COORDINATES[flow.from] || CONTINENT_COORDINATES['Unknown'];
    const toCoord = CONTINENT_COORDINATES[flow.to] || CONTINENT_COORDINATES['Unknown'];
    const isSelfFlow = flow.from === flow.to;
    const strokeWidth = Math.max(1, Math.min(8, (flow.value / maxValue) * 12));

    return (
      <React.Fragment key={`flow-group-${flow.from}-${flow.to}-${index}`}>
        {!isSelfFlow && (
          <MuiTooltip title={`${flow.from} → ${flow.to}: ${formatNumber(flow.value, 2)}`} arrow placement="top">
            <MapLine
              from={fromCoord}
              to={toCoord}
              stroke={CHART_COLORS.primary}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              opacity={0.6}
            />
          </MuiTooltip>
        )}

        {/* Origin Marker */}
        <MapMarker coordinates={fromCoord}>
          <circle r={3} fill={CHART_COLORS.secondary} opacity={0.7} />
        </MapMarker>

        {/* Destination Marker with Tooltip */}
        <MuiTooltip
          title={isSelfFlow ? `${flow.from} (Internal): ${formatNumber(flow.value, 2)}` : `${flow.to} (Destination)`}
          arrow
          placement="top"
        >
          <MapMarker coordinates={toCoord}>
            <circle r={isSelfFlow ? 6 : 4} fill={CHART_COLORS.primary} stroke="#fff" strokeWidth={2} />
          </MapMarker>
        </MuiTooltip>
      </React.Fragment>
    );
  };

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
        <ComposableMap
          projectionConfig={{
            scale: 147,
          }}
          style={{
            width: '100%',
            height: '100%',
          }}
        >
          <ZoomableGroup>
            <Geographies geography={mapData}>
              {({ geographies }: { geographies: any[] }) => geographies.map((geo) => renderGeography(geo))}
            </Geographies>

            {/* Render Flow Connections if data has from/to */}
            {chartConfig.data.some((item: any) => item.from && item.to) &&
              chartConfig.data.map((flow: any, index: number) => renderFlow(flow, index))}
          </ZoomableGroup>
        </ComposableMap>
      </Box>

      {/* Legend */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          mt: 1,
          mb: 1,
          flexWrap: 'wrap',
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 20,
              height: 20,
              background: `linear-gradient(to right, ${alpha(CHART_COLORS.primary, 0.3)}, ${CHART_COLORS.primary})`,
              borderRadius: 1,
              border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
            }}
          />
          <Typography variant="caption" color="text.secondary">
            Low to High Emissions
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 20,
              height: 20,
              background: alpha(theme.palette.divider, 0.1),
              borderRadius: 1,
              border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
            }}
          />
          <Typography variant="caption" color="text.secondary">
            No Data
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

interface AnalyticsProps {
  skipDefaultFilterFetch?: boolean;
}

const Analytics: React.FC<AnalyticsProps> = ({ skipDefaultFilterFetch = false }) => {
  const theme = useTheme();
  const useFilterContext = useFilters();
  const { setInfoHeaderDataExport, setTabDataExport, activeTabId, setActiveTabId } = useAnalyticsData();
  const { isSplitView } = useSplitView();
  const refresh = useAnalyticsRefresh();

  const [tabs, setTabs] = useState<TabInfo[]>([]);
  const [multilineChartTypes, setMultilineChartTypes] = useState<Record<string, 'multiline' | 'multibar'>>({});
  const [selectedDoughnutSlice, setSelectedDoughnutSlice] = useState<DoughnutSelection>(null);
  const [hoveredDoughnutSlice, setHoveredDoughnutSlice] = useState<DoughnutSelection>(null);
  const selectedTab = tabs.find(t => t.id === activeTabId) || (tabs.length > 0 ? tabs[0] : null);
  // Surface-level relabel: on the Sub-Customers tab, show "NA" instead of "main"
  const relabelSubCustomer = (value: any): any =>
    selectedTab?.name?.toLowerCase() === 'customers' && typeof value === 'string' && value.toLowerCase().includes('main')
      ? 'NA'
      : value;
  const [tabData, setTabData] = useState<any[]>([]);
  const [infoHeaderData, setInfoHeaderData] = useState<any[]>([]);

  const [infoHeaderIsLoading, setInfoHeaderIsLoading] = useState(true);
  const [tabDataIsLoading, setTabDataIsLoading] = useState(true);

  // Bridge the local main-screen loaders into the shared refresh barrier.
  // mainCycleIdRef mirrors the active cycle; wasMainLoadingRef tracks the last
  // combined-loading value so we can detect the settle (true -> false) transition.
  const mainCycleIdRef = useRef(0);
  mainCycleIdRef.current = refresh.cycleId;
  const wasMainLoadingRef = useRef(false);

  // Enlarged chart modal
  const [showEnlargeModal, setShowEnlargeModal] = useState(false);
  const [enlargedChartData, setEnlargedChartData] = useState<any>(null);

  // Map Topology State
  const [geoTopology, setGeoTopology] = useState<any>(null);
  const [continentFeatures, setContinentFeatures] = useState<any[]>([]);

  // Debounce timers
  let kpiTimeId: NodeJS.Timeout;
  let tabsTimerId: NodeJS.Timeout;

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const urlLicensee = searchParams.get('licensee');
  const isReportMode = searchParams.get('report_mode') === 'true';

  useEffect(() => {
    const applyResolvedDefaults = ({ tabId, timeFilter }: ResolvedDefaults): string | null => {
      if (tabId) {
        setActiveTabId(tabId);
      }
      if (!skipDefaultFilterFetch && timeFilter) {
        useFilterContext.changeTimeFilter({ key: timeFilter as any, value: timeFilter });
      }
      return tabId;
    };

    const initData = async () => {
      let defaultTabId = null;
      const urlTab = searchParams.get('tab');
      const urlType = searchParams.get('type');

      // 1. Fetch backend defaults, but we will selectively override if URLs exist.
      try {
        const { data } = await analyticsService.getData(analyticsService.endpoints.analyticsDefaultFilter, { page_key: 'analytics' });
        const dbResponse = data.code === 200 && data.response ? data.response : null;
        defaultTabId = applyResolvedDefaults(resolveInitDefaults(dbResponse, urlTab, urlType));
      } catch (error) {
        console.error('Failed to fetch default filters:', error);
        // On error, still apply URL params if they exist
        defaultTabId = applyResolvedDefaults(resolveInitDefaults(null, urlTab, urlType));
      }

      // 2. Licensee override from URL
      if (urlLicensee) {
        useFilterContext.setLicenseeData([{
          id: urlLicensee,
          key: 'licensee',
          type: 'licensee',
          value: `Licensee ID: ${urlLicensee}`
        }]);
      }

      getTabListData(defaultTabId);
    };

    initData();
    // Fetch topology data once
    fetch("https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json")
      .then(response => response.json())
      .then(topology => {
        setGeoTopology(topology);
      })
      .catch(err => console.error("Failed to load map topology:", err));
  }, []);

  // Debounced KPI header data fetching
  useEffect(() => {
    // In report mode, wait for the licensee filter to be applied before fetching
    if (isReportMode && urlLicensee && useFilterContext.licenseeData.length === 0) {
      return;
    }
    clearTimeout(kpiTimeId);
    setInfoHeaderIsLoading(true);
    kpiTimeId = setTimeout(() => {
      getInfoHeaderData();
    }, 1000);

    return () => clearTimeout(kpiTimeId);
  }, [
    useFilterContext.licenseeData,
    useFilterContext.subCompanyData,
    useFilterContext.branchData,
    useFilterContext.subCustomerData,
    useFilterContext.countryOriginData,
    useFilterContext.countryDestinationData,
    useFilterContext.cityOriginData,
    useFilterContext.cityDestinationData,
    useFilterContext.timeFilter,
    useFilterContext.fromToDate,
    useFilterContext.transportModeData,
  ]);

  // Debounced tab data fetching
  useEffect(() => {
    // In report mode, wait for the licensee filter to be applied before fetching
    if (isReportMode && urlLicensee && useFilterContext.licenseeData.length === 0) {
      return;
    }
    if (selectedTab) {
      clearTimeout(tabsTimerId);
      setTabDataIsLoading(true);
      tabsTimerId = setTimeout(() => {
        getTabData();
      }, 1000);

      return () => clearTimeout(tabsTimerId);
    }
  }, [
    selectedTab,
    useFilterContext.licenseeData,
    useFilterContext.subCompanyData,
    useFilterContext.branchData,
    useFilterContext.subCustomerData,
    useFilterContext.countryOriginData,
    useFilterContext.countryDestinationData,
    useFilterContext.cityOriginData,
    useFilterContext.cityDestinationData,
    useFilterContext.timeFilter,
    useFilterContext.fromToDate,
    useFilterContext.transportModeData,
  ]);

  // Register the main screen with the refresh coordinator so the panel waits for
  // it within one filter-refresh cycle. No-op without a provider (graceful default).
  useEffect(() => refresh.registerMain(), [refresh.registerMain]);

  // When both main loaders settle (true -> false), close out the active refresh
  // cycle so the shared barrier can clear once the panel is also done. The
  // coordinator stale-guards superseded cycles; time/tab-only refreshes target the
  // current cycle harmlessly because their main-pending was never armed.
  useEffect(() => {
    const loading = infoHeaderIsLoading || tabDataIsLoading;
    if (wasMainLoadingRef.current && !loading) {
      refresh.endMain(mainCycleIdRef.current);
    }
    wasMainLoadingRef.current = loading;
  }, [infoHeaderIsLoading, tabDataIsLoading, refresh.endMain]);

  const getTabListData = async (defaultTabId?: string | null) => {
    try {
      const { data } = await analyticsService.getData(analyticsService.endpoints.tabInfo);

      if (data.code === 200 && data.response.length > 0) {
        setTabs(data.response);
        if (defaultTabId) {
          setActiveTabId(defaultTabId);
        } else if (!activeTabId) {
          setActiveTabId(data.response[0].id);
        }
      }
    } catch (error) {
      console.error('Failed to fetch tabs:', error);
    }
  };

  const getInfoHeaderData = async () => {
    try {
      const payload = useFilterContext.buildFilterPayload();
      const { data } = await analyticsService.postData(
        analyticsService.endpoints.analyticsHeader,
        payload
      );

      if (data.code === 200) {
        setInfoHeaderData(data.response || []);
        setInfoHeaderDataExport(data.response || []);
      } else {
        setInfoHeaderData([]);
      }
    } catch (error) {
      console.error('Failed to fetch header data:', error);
      setInfoHeaderData([]);
    } finally {
      setInfoHeaderIsLoading(false);
    }
  };

  const getTabData = async () => {
    try {
      const payload = useFilterContext.buildFilterPayload({
        tab_id: selectedTab?.id,
        tab_type: selectedTab?.id
      });
      const { data } = await analyticsService.postData(
        analyticsService.endpoints.tabData,
        payload
      );

      if (data.code === 200) {
        setTabData(data.response || []);
        setTabDataExport(data.response || []);
      } else {
        setTabData([]);
      }
    } catch (error) {
      console.error('Failed to fetch tab data:', error);
      setTabData([]);
    } finally {
      setTabDataIsLoading(false);
    }
  };

  const handleTabSelect = (tab: TabInfo) => {
    if (!tabDataIsLoading) {
      setActiveTabId(tab.id);
    }
  };

  const handleEnlarge = (chartItem: any) => {
    setEnlargedChartData(chartItem);
    setShowEnlargeModal(true);
  };

  const activeDoughnutSlice = hoveredDoughnutSlice ?? selectedDoughnutSlice;

  useEffect(() => {
    setHoveredDoughnutSlice(null);
    setSelectedDoughnutSlice(null);
  }, [activeTabId, tabData]);

  const isToggleableMultilineChart = (name?: string) =>
    !!name && name.toLowerCase().includes('by calendar month');

  // Shared chart fragments (extracted to remove duplicated render blocks).
  // Recharts 3 flattens fragments when reading children, so these behave
  // identically to inlining the elements directly.
  const renderCartesianAxes = () => (
    <>
      <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.2)} />
      <XAxis
        dataKey="name"
        tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
        axisLine={{ stroke: alpha(theme.palette.divider, 0.3) }}
      />
      <YAxis
        tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
        axisLine={{ stroke: alpha(theme.palette.divider, 0.3) }}
        tickFormatter={(value) => formatChartNumber(Number(value))}
      />
    </>
  );

  const renderSimpleChartTooltip = () => (
    <Tooltip
      contentStyle={{
        background: 'white',
        border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
        borderRadius: 8,
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      }}
      formatter={(val: any, name: any) => [formatChartNumber(Number(val) || 0, 2), name]}
    />
  );

  const renderDetailedChartTooltip = () => (
    <Tooltip
      contentStyle={{
        background: 'white',
        border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
        borderRadius: 8,
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        padding: '6px 10px',
      }}
      itemStyle={{ padding: '1px 0', margin: 0, fontSize: '0.75rem' }}
      labelStyle={{ margin: 0, paddingBottom: '2px', fontWeight: 600, fontSize: '0.85rem' }}
      wrapperStyle={{ zIndex: 1000 }}
      formatter={(val: any, name: any) => [formatChartNumber(Number(val) || 0, 2), name]}
    />
  );

  const renderChart = (chartConfig: any, isEnlarged = false, chartTypeOverride?: string, chartInstanceKey = 'chart') => {
    const height = isEnlarged ? 500 : 350;

    const isCustomersTab = selectedTab?.name?.toLowerCase() === 'customers';
    chartConfig = aliasChartConfigData(chartConfig, isCustomersTab);

    if (!chartConfig?.data || chartConfig.data.length === 0) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height,
            background: alpha(theme.palette.divider, 0.05),
            borderRadius: 2,
          }}
        >
          <Typography color="text.secondary">No data available</Typography>
        </Box>
      );
    }

    const chartProps = {
      margin: { top: 20, right: 30, left: 20, bottom: 20 },
    };

    const effectiveType = chartTypeOverride || chartConfig.type;
    switch (effectiveType) {
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={height}>
            <BarChart data={chartConfig.data} {...chartProps}>
              {renderCartesianAxes()}
              {renderSimpleChartTooltip()}
              <Legend wrapperStyle={{ paddingTop: 20 }} />
              <Bar
                dataKey={chartConfig.data[0]?.emissions === undefined ? "value" : "emissions"}
                fill={CHART_COLORS.primary}
                name={chartConfig.data[0]?.emissions === undefined ? "Value" : "Emissions (kg CO2e)"}
                radius={[4, 4, 0, 0]}
              />
              {chartConfig.data[0]?.weight !== undefined && (
                <Bar
                  dataKey="weight"
                  fill={CHART_COLORS.secondary}
                  name="Weight (kg)"
                  radius={[4, 4, 0, 0]}
                />
              )}
            </BarChart>
          </ResponsiveContainer>
        );

      case 'pie':
        return <PieChartView chartConfig={chartConfig} height={height} isEnlarged={isEnlarged} theme={theme} />;

      case 'doughnut':
        return (
          <DoughnutChartView
            chartConfig={chartConfig}
            height={height}
            isEnlarged={isEnlarged}
            chartInstanceKey={chartInstanceKey}
            theme={theme}
            activeDoughnutSlice={activeDoughnutSlice}
            setHoveredDoughnutSlice={setHoveredDoughnutSlice}
            setSelectedDoughnutSlice={setSelectedDoughnutSlice}
          />
        );

      case 'line':
        return (
          <ResponsiveContainer width="100%" height={height}>
            <LineChart data={chartConfig.data} {...chartProps}>
              {renderCartesianAxes()}
              {renderSimpleChartTooltip()}
              <Legend wrapperStyle={{ paddingTop: 20 }} />
              <Line
                type="monotone"
                dataKey="value"
                stroke={CHART_COLORS.primary}
                strokeWidth={3}
                name={chartConfig.lineName}
                dot={{ fill: CHART_COLORS.primary, r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        );

      case 'area':
        return (
          <ResponsiveContainer width="100%" height={height}>
            <AreaChart data={chartConfig.data} {...chartProps}>
              {renderCartesianAxes()}
              {renderSimpleChartTooltip()}
              <Legend wrapperStyle={{ paddingTop: 20 }} />
              <Area
                type="monotone"
                dataKey="value"
                stroke={CHART_COLORS.primary}
                strokeWidth={3}
                name={chartConfig.areaName}
                dot={{ fill: CHART_COLORS.primary, r: 4 }}
                activeDot={{ r: 6 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        );

      case 'multiline': {
        // Get line keys dynamically (Road, Sea, Air, etc.)
        const lineKeys = Object.keys(chartConfig.data[0]).filter(key => key !== 'name');
        return (
          <ResponsiveContainer width="100%" height={height}>
            <LineChart data={chartConfig.data} {...chartProps}>
              {renderCartesianAxes()}
              {renderDetailedChartTooltip()}
              <Legend verticalAlign="top" wrapperStyle={{ paddingBottom: 20 }} />
              {lineKeys.map((key, index) => (
                <Line
                  key={key}
                  type="monotone"
                  dataKey={key}
                  name={key}
                  stroke={CHART_COLORS.chartColors[index] ?? CHART_COLORS.primary}
                  strokeWidth={3}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        );
      }

      case 'multibar': {
        const barKeys = Object.keys(chartConfig.data[0]).filter((key) => key !== 'name');
        return (
          <ResponsiveContainer width="100%" height={height}>
            <BarChart data={chartConfig.data} {...chartProps}>
              {renderCartesianAxes()}
              {renderDetailedChartTooltip()}
              <Legend verticalAlign="top" wrapperStyle={{ paddingBottom: 20 }} />
              {barKeys.map((key, index) => (
                <Bar
                  key={key}
                  dataKey={key}
                  name={key}
                  fill={CHART_COLORS.chartColors[index] ?? CHART_COLORS.primary}
                  radius={[4, 4, 0, 0]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        );
      }

      case 'map':
        return (
          <MapChartView
            chartConfig={chartConfig}
            theme={theme}
            continentFeatures={continentFeatures}
            geoTopology={geoTopology}
            setContinentFeatures={setContinentFeatures}
          />
        );

      default:
        return (
          <Typography color="text.secondary">
            Unsupported chart type: {chartConfig.type}
          </Typography>
        );
    }
  };

  // Renders a chart card (primary or secondary) inside the analytics grid.
  // Extracted from two near-identical blocks; differs only by the chart
  // source, the non-split grid width and the render instance-key role.
  const renderTabChartCard = (chart: any, nonSplitLg: number, role: string, index: number) => {
    const toggleable = isToggleableMultilineChart(chart.name);
    const overrideType = multilineChartTypes[chart.name] || 'multiline';
    return (
      <Grid item xs={12} lg={isSplitView ? 12 : nonSplitLg}>
        <ProfessionalChartCard
          title={formatAnalyticsLabel(chart.name)}
          onFullscreen={() => handleEnlarge(chart)}
          height={350}
          headerAction={toggleable ? (
            <ToggleButtonGroup
              value={overrideType}
              exclusive
              size="small"
              onChange={(_, v) => v && setMultilineChartTypes(prev => ({ ...prev, [chart.name]: v }))}
            >
              <ToggleButton value="multiline" sx={{ px: 1.5, py: 0.25, textTransform: 'none' }}>Line</ToggleButton>
              <ToggleButton value="multibar" sx={{ px: 1.5, py: 0.25, textTransform: 'none' }}>Bar</ToggleButton>
            </ToggleButtonGroup>
          ) : undefined}
        >
          {renderChart(chart, false, toggleable ? overrideType : undefined, `${activeTabId || 'tab'}-${role}-${index}`)}
        </ProfessionalChartCard>
      </Grid>
    );
  };

  // Renders the CATEGORY / VALUE header cells shared by both side tables.
  // The only difference between the two original headers was the background.
  const renderTabTableHeaderCells = (headerBackground: string) => (
    <>
      <TableCell
        sx={{
          background: headerBackground,
          fontWeight: 700,
          fontSize: '0.75rem',
          letterSpacing: '0.5px',
          color: theme.palette.text.primary,
          borderBottom: `2px solid ${CHART_COLORS.primary}`,
        }}
      >
        CATEGORY
      </TableCell>
      <TableCell
        align="right"
        sx={{
          background: headerBackground,
          fontWeight: 700,
          fontSize: '0.75rem',
          textTransform: 'none !important',
          letterSpacing: '0.5px',
          color: theme.palette.text.primary,
          borderBottom: `2px solid ${CHART_COLORS.primary}`,
        }}
      >
        VALUE (TONS CO2e)
      </TableCell>
    </>
  );

  // Renders the body rows shared by both analytics side tables.
  const renderTabTableRows = (rows: any[]) =>
    rows.map((row: any) => (
      <TableRow
        key={row.col1}
        sx={{
          transition: 'all 0.2s ease-in-out',
          '&:nth-of-type(even)': {
            background: alpha(theme.palette.divider, 0.02),
          },
          '&:hover': {
            background: alpha(CHART_COLORS.primary, 0.05),
            transform: 'scale(1.005)',
          },
        }}
      >
        <TableCell sx={{ fontSize: '0.875rem', fontWeight: 500 }}>{alpha3ToAlpha2(relabelSubCustomer(row.col1))}</TableCell>
        <TableCell align="right" sx={{ fontSize: '0.875rem', fontWeight: 600, color: CHART_COLORS.primary }}>
          {row.col2}
        </TableCell>
      </TableRow>
    ));

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: 'linear-gradient(135deg, #fafbfc 0%, #f4f6f8 40%, #e8f4f2 100%)',
        position: 'relative',
        '@media print': {
          height: 'auto',
          background: 'white',
          overflow: 'visible',
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 50%, rgba(144, 191, 69, 0.03) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(108, 118, 124, 0.02) 0%, transparent 50%)',
          pointerEvents: 'none',
        },
      }}
    >
      {/* KPI Cards */}
      <Box
        sx={{
          p: { xs: 4, print: 2 },
          position: 'relative',
          zIndex: 1,
          overflowX: 'visible',
          pb: isSplitView ? 2 : 4, // More space for scrollbar if needed
          '@media print': { pb: 1 },
          '&::-webkit-scrollbar': {
            height: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: alpha(theme.palette.divider, 0.1),
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: alpha(theme.palette.divider, 0.2),
          },
        }}
      >
        {(() => {
          if (infoHeaderIsLoading || refresh.isFilterRefreshing) {
            return (
          <Box
            sx={{
              display: isSplitView ? 'flex' : 'grid',
              flexWrap: 'wrap',
              gap: 3,
              pb: isSplitView ? 1 : 0,
              width: '100%',
              gridTemplateColumns: isSplitView
                ? 'none'
                : { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
            }}
          >
            {['kpi-skeleton-0', 'kpi-skeleton-1', 'kpi-skeleton-2', 'kpi-skeleton-3'].map((key, idx) => (
              <KpiSkeletonCard key={key} idx={idx} isSplitView={isSplitView} />
            ))}
          </Box>
            );
          }
          if (infoHeaderData.length > 0) {
            return (
          <Box
            sx={{
              display: isSplitView ? 'flex' : 'grid',
              flexWrap: 'wrap',
              gap: 3,
              pb: isSplitView ? 1 : 0,
              width: '100%',
              gridTemplateColumns: isSplitView
                ? 'none'
                : { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
            }}
          >
            {infoHeaderData.filter((item) => !item.label.toLowerCase().includes('compensat')).map((item, index) => {
              const icons = [
                <Co2Icon key="info-icon-0" />,
                <Co2Icon key="info-icon-1" />,
                <Co2Icon key="info-icon-2" />,
                <Co2Icon key="info-icon-3" />,
              ];
              const colors = [
                CHART_COLORS.primary,
                CHART_COLORS.info,
                CHART_COLORS.warning,
                CHART_COLORS.success,
              ];

              const isUnavailable = typeof item.value === 'string' && item.value.toUpperCase().startsWith('N/A');
              return (
                <Box key={item.label} sx={{ minWidth: isSplitView ? 180 : 'auto', flex: isSplitView ? '1 1 250px' : '1 1 auto' }}>
                  <ProfessionalKPICard
                    label={item.label}
                    subLabel={item.subLabel}
                    value={isUnavailable ? '-' : formatStringValue(convertTransportModeAlias(convertValueCountryCode(item.value)))}
                    unit={isUnavailable ? undefined : item.unit}
                    icon={icons[index % icons.length]}
                    color={colors[index % colors.length]}
                  />
                </Box>
              );
            })}
          </Box>
            );
          }
          return null;
        })()}
      </Box>

      {/* Tabs */}
      <Box
        sx={{
          px: { xs: 4, print: 2 },
          position: 'relative',
          zIndex: 1,
          background: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(20px) saturate(180%)',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.04)}`,
        }}
      >
        <Tabs
          value={selectedTab?.id || ''}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          onChange={(_, value) => {
            const tab = tabs.find((t) => t.id === value);
            if (tab) handleTabSelect(tab);
          }}
          sx={{
            flex: 1,
            '& .MuiTab-root': {
              fontWeight: 600,
              fontSize: '0.9rem',
              textTransform: 'none',
              minHeight: 56,
              px: 3,
              color: theme.palette.text.secondary,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&.Mui-selected': {
                color: CHART_COLORS.primary,
                fontWeight: 700,
              },
              '&:hover': {
                color: CHART_COLORS.primary,
                background: alpha(CHART_COLORS.primary, 0.04),
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: CHART_COLORS.primary,
              height: 3,
              borderRadius: '3px 3px 0 0',
              boxShadow: `0 -2px 8px ${alpha(CHART_COLORS.primary, 0.4)}`,
            },
          }}
        >
          {tabs.map((tab) => (
            <Tab key={tab.id} label={`${tab.icon} ${formatAnalyticsLabel(tab.name)}`} value={tab.id} />
          ))}
        </Tabs>
      </Box>

      <Box sx={{ flex: 1, p: { xs: 4, print: 2 }, position: 'relative', zIndex: 1, overflowY: 'auto' }}>
        {(() => {
          if (tabDataIsLoading || refresh.isFilterRefreshing) {
            return (
          <Grid container spacing={3}>
            {/* Primary chart + side table */}
            <Grid item xs={12} lg={isSplitView ? 12 : 8}>
              <Paper sx={{ p: 3, height: 410 }}>
                <Skeleton variant="text" width={220} sx={{ fontSize: '1.125rem', mb: 2 }} />
                <Skeleton variant="rounded" width="100%" height={330} />
              </Paper>
            </Grid>
            <Grid item xs={12} lg={isSplitView ? 12 : 4}>
              <Paper sx={{ p: 0, height: 410, overflow: 'hidden' }}>
                <Box sx={{ p: 2.5, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                  <Skeleton variant="text" width="70%" sx={{ fontSize: '1rem' }} />
                </Box>
                <Box sx={{ px: 2.5, py: 1.5, display: 'flex', justifyContent: 'space-between' }}>
                  <Skeleton variant="text" width={80} sx={{ fontSize: '0.75rem' }} />
                  <Skeleton variant="text" width={100} sx={{ fontSize: '0.75rem' }} />
                </Box>
                {Array.from({ length: 7 }, (_, i) => `row-${i}`).map((rowKey, idx) => (
                  <Box
                    key={rowKey}
                    sx={{
                      px: 2.5,
                      py: 1.25,
                      display: 'flex',
                      justifyContent: 'space-between',
                      borderTop: `1px solid ${alpha(theme.palette.divider, 0.05)}`,
                    }}
                  >
                    <Skeleton variant="text" width={`${40 + ((idx * 9) % 30)}%`} />
                    <Skeleton variant="text" width={70} />
                  </Box>
                ))}
              </Paper>
            </Grid>
            {/* Secondary chart + side table */}
            <Grid item xs={12} lg={isSplitView ? 12 : 4}>
              <Paper sx={{ p: 3, height: 410 }}>
                <Skeleton variant="text" width={180} sx={{ fontSize: '1.125rem', mb: 2 }} />
                <Skeleton variant="circular" width={220} height={220} sx={{ mx: 'auto', mt: 4 }} />
              </Paper>
            </Grid>
            <Grid item xs={12} lg={isSplitView ? 12 : 8}>
              <Paper sx={{ p: 3, height: 410 }}>
                <Skeleton variant="text" width={220} sx={{ fontSize: '1.125rem', mb: 2 }} />
                <Skeleton variant="rounded" width="100%" height={330} />
              </Paper>
            </Grid>
          </Grid>
            );
          }
          if (tabData.length > 0) {
            return (
          <Grid container spacing={3}>
            {tabData.filter((item) => {
              const name = (
                (item.primaryChart?.name || '') + ' ' +
                (item.secondaryChart?.name || '') + ' ' +
                (item.tableData?.data?.primaryTable?.name || '') + ' ' +
                (item.tableData?.data?.secondaryTable?.name || '')
              ).toLowerCase();
              if (name.includes('compensat')) return false;
              if (name.includes('transport mode') && name.includes('weight')) return false;
              if (name.includes('transport modes') && name.includes('weight')) return false;
              return true;
            }).map((tabDataItem, index) => (
              <React.Fragment key={tabDataItem.primaryChart?.name ?? tabDataItem.secondaryChart?.name ?? 'tab-item'}>
                {/* Primary Chart */}
                {tabDataItem.primaryChart &&
                  renderTabChartCard(tabDataItem.primaryChart, 8, 'primary', index)}

                {/* Primary Table */}
                {tabDataItem.tableData?.data.primaryTable && (
                  <Grid item xs={12} lg={isSplitView ? 12 : 4}>
                    <Paper
                      elevation={0}
                      sx={{
                        height: '100%',
                        background: 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(20px) saturate(180%)',
                        border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                        borderRadius: 3,
                        overflow: 'hidden',
                        transition: 'all 0.3s ease-in-out',
                        '&:hover': {
                          boxShadow: `0 12px 24px ${alpha(theme.palette.primary.main, 0.1)}`,
                          border: `1px solid ${alpha(CHART_COLORS.primary, 0.2)}`,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          p: 2.5,
                          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                          background: `linear-gradient(135deg, ${alpha(CHART_COLORS.primary, 0.04)}, transparent)`,
                        }}
                      >
                        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', color: 'text.primary' }}>
                          {formatAnalyticsLabel(tabDataItem.tableData.data.primaryTable.name)}
                        </Typography>
                      </Box>
                      <TableContainer sx={{ maxHeight: 360, borderRadius: 0 }}>
                        <Table size="small" stickyHeader>
                          <TableHead
                            sx={{
                              marginTop: -1,
                            }}
                          >
                            <TableRow>
                              {renderTabTableHeaderCells(alpha(CHART_COLORS.optionBG, 1))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {renderTabTableRows(tabDataItem.tableData.data.primaryTable.data)}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  </Grid>
                )}

                {/* Secondary Chart */}
                {tabDataItem.secondaryChart &&
                  renderTabChartCard(tabDataItem.secondaryChart, 4, 'secondary', index)}

                {/* Secondary Table */}
                {tabDataItem.tableData?.data.secondaryTable && (
                  <Grid
                    item
                    xs={12}
                    lg={(() => {
                      if (isSplitView) return 12;
                      return tabDataItem.primaryChart && tabDataItem.tableData?.data.primaryTable ? 4 : 6;
                    })()}
                    sx={tabDataItem.primaryChart && tabDataItem.tableData?.data.primaryTable ? { ml: 'auto' } : undefined}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        height: '100%',
                        background: 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(20px) saturate(180%)',
                        border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                        borderRadius: 3,
                        overflow: 'hidden',
                        transition: 'all 0.3s ease-in-out',
                        '&:hover': {
                          boxShadow: `0 12px 24px ${alpha(theme.palette.primary.main, 0.1)}`,
                          border: `1px solid ${alpha(CHART_COLORS.primary, 0.2)}`,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          p: 2.5,
                          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                          background: `linear-gradient(135deg, ${alpha(CHART_COLORS.primary, 0.04)}, transparent)`,
                        }}
                      >
                        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', color: 'text.primary' }}>
                          {formatAnalyticsLabel(tabDataItem.tableData.data.secondaryTable.name)}
                        </Typography>
                      </Box>
                      <TableContainer sx={{ maxHeight: 350 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              {renderTabTableHeaderCells(alpha(CHART_COLORS.primary, 0.08))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {renderTabTableRows(tabDataItem.tableData.data.secondaryTable.data)}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  </Grid>
                )}
              </React.Fragment>
            ))}
          </Grid>
            );
          }
          return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              py: 12,
            }}
          >
            <Co2Icon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No data available for this selection
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Try adjusting your filters to see analytics
            </Typography>
          </Box>
          );
        })()}
      </Box>

      {/* Enlarged Chart Modal */}
      <Dialog
        open={showEnlargeModal}
        onClose={() => setShowEnlargeModal(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px) saturate(180%)',
            boxShadow: `0 24px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          },
        }}
        sx={{
          '& .MuiBackdrop-root': {
            backdropFilter: 'blur(8px)',
            backgroundColor: alpha(theme.palette.grey[900], 0.6),
          },
        }}
      >
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, ${alpha(CHART_COLORS.primary, 0.06)}, transparent)`,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            pb: 2,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
                {enlargedChartData?.name}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Detailed visualization
              </Typography>
            </Box>
            <IconButton
              onClick={() => setShowEnlargeModal(false)}
              sx={{
                color: 'text.secondary',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  background: alpha(CHART_COLORS.primary, 0.15),
                  color: CHART_COLORS.primary,
                  transform: 'rotate(90deg)',
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 3 }}>
          {enlargedChartData && renderChart(
            enlargedChartData,
            true,
            isToggleableMultilineChart(enlargedChartData.name)
              ? (multilineChartTypes[enlargedChartData.name] || 'multiline')
              : undefined,
            `${activeTabId || 'tab'}-enlarged`
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

// KPI loading skeleton card. The identical block also exists in Dashboard;
// see the note there explaining why it is duplicated rather than shared.
const KpiSkeletonCard: React.FC<{ idx: number; isSplitView: boolean }> = ({ idx, isSplitView }) => (
  <Box key={`kpi-skeleton-${idx}`} sx={{ minWidth: isSplitView ? 180 : 'auto', flex: isSplitView ? '1 1 250px' : '1 1 auto' }}>
    <Paper sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
      <Skeleton variant="circular" width={48} height={48} />
      <Box sx={{ flex: 1 }}>
        <Skeleton variant="text" width="70%" sx={{ fontSize: '0.75rem' }} />
        <Skeleton variant="text" width="55%" sx={{ fontSize: '1.5rem' }} />
        <Skeleton variant="text" width="40%" sx={{ fontSize: '0.7rem' }} />
      </Box>
    </Paper>
  </Box>
);

export default Analytics;
